import { useState } from "react";
import { Link } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { TextLink } from "../components/kit/TextLink";
import { BrandMark } from "../components/brand/BrandMark";

/**
 * ForgotPasswordNew — issue #874, slice S3, the forgotten-password REQUEST
 * half redrawn to `ForgottenPassword.dc.html` (design-baseline-round-5).
 * Lives behind `newUi("signin")` (`App.js`, reusing S1/S2's flag — this
 * slice adds no new one); `ForgotPassword.jsx`, the shipped screen, is
 * untouched and keeps serving `/forgot-password` with the flag off.
 *
 * PRESENTATION ONLY. Posts to the SAME endpoint (`POST /auth/forgot-
 * password`) the shipped screen does, with the same anti-enumeration
 * contract: the response is one constant message whether or not the email
 * is registered, rendered verbatim and nothing else, never a token or a
 * link. Nothing here calls a different endpoint or widens what the server
 * accepts.
 *
 * CHROME. The drawing draws its own header (mark, "Compass") outside the
 * app shell — no TopBar, no nav bar (#730, same as every other redrawn
 * entry screen). Drawn with no `href`/button styling of its own — X-001
 * ("nothing invented"): the header stays a plain, non-interactive mark,
 * the same call `SignInLinkRequestNew.jsx` made for the identical drawn
 * shape.
 *
 * D48/D57 — ONE REAL BEHAVIOUR CHANGE, PRESENTATION ONLY. The shipped
 * screen toasts `formatApiErrorDetail(...) || "Request failed"` on a
 * failed request and leaves the form exactly as it was. The drawing draws
 * a fourth state, `request-failed`, that the shipped screen has no
 * equivalent of: the server's own words stay on the page, inline, with the
 * D55 marks — no toast. The form (including the email field and the
 * persistent "60 minutes" tile) stays visible and resubmittable; only the
 * SENT state (below) replaces it — the same shape S2's
 * `SignInLinkRequestNew.jsx` already established for the identical drawn
 * pattern.
 *
 * DESIGNERS' NOTES ARE NOT COPY (S1's B1, checked again for this slice):
 * every `data-dc-note` block in `ForgottenPassword.dc.html` — the
 * dashed-border state captions and the trailing rationale paragraphs — are
 * checked against every state below and NONE of them render. Only the
 * SOLID-cornered content nested inside each dashed block is real UI.
 */

export default function ForgotPasswordNew() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // D48/D57 — the request's own failure message, read from the server's
  // response, never a toast. Cleared at the start of every new submit so a
  // stale banner cannot survive past the attempt that would replace it.
  const [requestError, setRequestError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setRequestError(null);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (err) {
      setRequestError(formatApiErrorDetail(err.response?.data?.detail) || "Request failed");
    }
    setBusy(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--nw-page)" }}>
      <span data-testid="forgot-password-request-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />

      <div
        data-testid="forgot-password-page"
        className="nw-column"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--nw-space-stack)",
          padding: "var(--nw-space-page)",
          color: "var(--nw-ink)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--nw-ink)" }}>
          <BrandMark size={24} />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-navy)" }}>
            Compass
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* R2 lesson (S2) — scoped to !result alongside the form it
              labels: no alternate eyebrow is drawn for "sent", and "ask
              for the link" stops being true once a link has been asked
              for and sent. */}
          {!result && <div className="nw-eyebrow">Step 1 of 2 · ask for the link</div>}
          <div className="nw-title">Reset your password</div>
          <div className="nw-lede">
            Enter the email tied to your CricketCompass account and we'll email you a reset link.
          </div>
        </div>

        {!result && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-card)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  padding: "4px 10px",
                  background: "var(--nw-line-faint)",
                  border: "1px solid var(--nw-line)",
                  borderRadius: "var(--nw-radius-control)",
                }}
              >
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 18, lineHeight: 1.1, color: "var(--nw-ink)" }}>
                  60
                </span>
              </div>
              <span className="nw-help">
                minutes — how long the link works for. After that it is dead and you ask for another.
              </span>
            </div>

            <form onSubmit={submit} data-testid="forgot-password-form" style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-stack)", color: "var(--nw-ink)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {/* Review round 3, final narrow round (A3), F9: real
                    <label htmlFor>, same fix as kit/Field.jsx's own
                    (#902/#907). */}
                <label htmlFor="fp-email" className="nw-label" style={{ display: "block" }}>Email</label>
                <input
                  id="fp-email"
                  data-testid="fp-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="nw-kit-input"
                  style={{
                    width: "100%",
                    height: "var(--nw-size-control)",
                    borderRadius: "var(--nw-radius-control)",
                    border: "1px solid var(--nw-line)",
                    background: "var(--nw-surface)",
                    padding: "0 12px",
                    fontSize: 15,
                    color: "var(--nw-ink)",
                  }}
                />
              </div>

              {requestError && (
                <div
                  role="alert"
                  data-testid="fp-request-error"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "var(--nw-space-inline)",
                    padding: "10px 12px",
                    background: "var(--nw-line-faint)",
                    borderLeft: "3px solid var(--nw-alert)",
                    borderRadius: "var(--nw-radius-control)",
                  }}
                >
                  <TriangleAlert size={14} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
                  <div data-testid="fp-request-error-message" style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>
                    {requestError}
                  </div>
                </div>
              )}

              <button
                type="submit"
                data-testid="fp-submit"
                disabled={busy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: busy ? 10 : 0,
                  minHeight: "var(--nw-size-action)",
                  background: "var(--nw-gold)",
                  color: "var(--nw-ink)",
                  fontWeight: 700,
                  fontSize: 16,
                  border: "none",
                  borderRadius: "var(--nw-radius-control)",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                {busy && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--nw-ink)" strokeWidth="2.5" strokeLinecap="round" className="animate-spin" aria-hidden="true">
                    <path d="M12 3a9 9 0 1 0 9 9" />
                  </svg>
                )}
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        )}

        {result && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div
              data-testid="fp-result"
              style={{
                padding: "10px 12px",
                background: "var(--nw-line-faint)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-control)",
              }}
            >
              <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>{result.message}</div>
              <div className="nw-help" style={{ marginTop: 6 }}>
                If it doesn't arrive within a few minutes, check your spam folder before requesting another.
              </div>
            </div>
            <button
              type="button"
              data-testid="fp-send-another"
              onClick={() => {
                setResult(null);
                setEmail("");
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "var(--nw-size-chip)",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                color: "var(--nw-navy)",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "var(--nw-radius-control)",
                cursor: "pointer",
              }}
            >
              Send another
            </button>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
          {/* R4 (S2's own review rule) — a literal, not an imported
              constant: this file's only /login reference. */}
          <TextLink
            as={Link}
            to="/login"
            data-testid="fp-back"
            style={{ gap: 6, fontSize: 13, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "none" }}
          >
            ← Back to sign in
          </TextLink>
        </div>
      </div>
    </div>
  );
}
